'use client';

import type { MouseEvent } from 'react';
import { useTranslations } from 'next-intl';
import { showDataGrailConsentPreferences } from '@zg-rentals/datagrail';
import { Anchor } from '@zillow/constellation-10';
import AppleBadge from '@/src/components/ui/icons/AppleBadge';
import AndroidBadge from '@/src/components/ui/icons/AndroidBadge';
import HudIcon from '@/src/components/ui/icons/HudIcon';
import { appBadges, type FooterNavItem, legalLinks, useFooterLinks } from './footerLinks';
import {
  badgeImg,
  badgeSection,
  copyrightSection,
  crossbrandItem,
  crossbrandNav,
  fairHousingLink,
  footer,
  footerNav,
  hudIcon,
  linkButton,
  linkItem,
  linkList,
  textSection,
} from './footer.styles';

type FooterProps = {
  showCrossbrand?: boolean;
  showAppBadges?: boolean;
};

export default function Footer({ showCrossbrand = true, showAppBadges = true }: FooterProps) {
  const t = useTranslations('footer');
  const { primaryLinks, secondaryLinks, crossbrandLinks } = useFooterLinks();
  const handleCookiePreferenceClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    showDataGrailConsentPreferences();
  };

  return (
    <footer className={footer}>
      {showCrossbrand && (
        <nav aria-label={t('zillowGroupBrandsAria')}>
          <ul className={crossbrandNav}>
            {crossbrandLinks.map((link) => (
              <li key={link.href} className={crossbrandItem}>
                <Anchor href={link.href} target="_blank" rel="noopener noreferrer" onImpact>
                  {link.label}
                </Anchor>
              </li>
            ))}
          </ul>
        </nav>
      )}

      <nav aria-label={t('footerNav')} className={footerNav}>
        <ul className={linkList}>
          {primaryLinks.map((link) => (
            <li key={getFooterNavItemKey(link)} className={linkItem}>
              {renderFooterNavItem(link, handleCookiePreferenceClick)}
            </li>
          ))}
        </ul>
        <ul className={linkList}>
          {secondaryLinks.map((link) => (
            <li key={getFooterNavItemKey(link)} className={linkItem}>
              {renderFooterNavItem(link, handleCookiePreferenceClick)}
            </li>
          ))}
        </ul>
      </nav>

      {showAppBadges && (
        <div className={badgeSection}>
          <Anchor href={appBadges.ios.href} target="_blank" rel="noopener noreferrer nofollow">
            <AppleBadge className={badgeImg} />
          </Anchor>
          <Anchor href={appBadges.android.href} target="_blank" rel="noopener noreferrer nofollow">
            <AndroidBadge className={badgeImg} />
          </Anchor>
        </div>
      )}

      <p className={textSection}>
        {t.rich('accessibility', {
          email: (chunks) => (
            <Anchor href="mailto:support@hotpads.com" onImpact>
              {chunks}
            </Anchor>
          ),
        })}
      </p>

      <p className={textSection}>
        <Anchor href={legalLinks.hud} target="_blank" rel="noopener noreferrer" onImpact className={fairHousingLink}>
          {t('fairHousing')}
          <HudIcon className={hudIcon} />
        </Anchor>
      </p>

      <p className={textSection}>
        <Anchor href={legalLinks.fairHousingGuide} target="_blank" rel="noopener noreferrer" onImpact>
          {t('fairHousingGuide')}
        </Anchor>
      </p>

      <p className={copyrightSection}>{t('copyright', { year: new Date().getFullYear() })}</p>
    </footer>
  );
}

function getFooterNavItemKey(item: FooterNavItem) {
  return 'href' in item ? item.href : item.action;
}

function renderFooterNavItem(
  item: FooterNavItem,
  onCookiePreferenceClick: (event: MouseEvent<HTMLButtonElement>) => void,
) {
  if ('action' in item) {
    return (
      <button type="button" className={linkButton} onClick={onCookiePreferenceClick}>
        {item.label}
      </button>
    );
  }

  return (
    <Anchor href={item.href} onImpact {...(item.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
      {item.label}
    </Anchor>
  );
}
